import * as Models from "../ts/rlAdmin.models";
import * as Consts from "../ts/rlAdmin.consts";
import * as Utils from "../ts/rlAdmin.utils";
import * as angular from "angular";

import { EntityService } from "./entity-service";
import { NotifyService } from "./notify-service";
import { EditEntityModalController } from "../controllers/modals/edit-entity-modal-controller";
import { BulkEditModalController } from "../controllers/modals/bulk-edit-modal-controller";
import { JobProgressModalController } from "../controllers/modals/job-progress-modal-controller";
import { BulkExecuteWorkFlowModalController } from "../Controllers/modals/bulk-execute-wf-modal";

export class ModalService {
    public static $inject = ['$q', '$uibModal', '$timeout', 'entityService', 'notifyService'];

    public constructor(
        private readonly $q: ng.IQService,
        private readonly $uibModalService: ng.ui.bootstrap.IModalService,
        private readonly $timeout: ng.ITimeoutService,
        private readonly entityService: EntityService,
        private readonly notifyService: NotifyService) {
    }

    createEntityByTemplate(templateID: string, parentEntityID?: string,
        parentCurrencyCharData?: Models.ICharDataValue, relationshipCharTypeIDs?: number[],
        requiredFieldsOnly: boolean = false, copyCmd?: Models.ICharDataCollection, singleSelect: boolean = false)
        : ng.IPromise<{ entityID: string, relRecID: number }> {

        var deferred = this.$q.defer<any>();

        this.entityService.getEmptyEntityCharData(templateID)
            .then((res) => {

                if (copyCmd) {
                    _.forOwn(copyCmd.charDatas, (charData, tagLabel) => {
                        let newCmd = _.find(res.templateMetaData.characteristicMetaDatas, (newCmd) => {
                            return newCmd.tagLabel == tagLabel;
                        });
                        if (tagLabel.indexOf('entity') == -1 && res.charDatas[tagLabel]) {
                            res.charDatas[tagLabel] = _.clone(charData);
                            res.charDatas[tagLabel].forEach((cd) => {
                                cd.recCharID = 0;
                                cd.charID = newCmd.characteristicID;
                            })
                        }
                    });
                }

                this.editEntityWithRelationships(res, parentEntityID, parentCurrencyCharData, null, null, relationshipCharTypeIDs, requiredFieldsOnly, singleSelect)
                    .then((cdc) => {
                        deferred.resolve(cdc);
                    })
                    .catch((err) => {
                        deferred.reject(err);
                    })
            })
            .catch(this.notifyService.errorResponseHandler);

        return deferred.promise;
    }

    createEntity(charTypeID: number, templateID: number,
        parentEntityID?: string, parentCurrencyCharData?: Models.ICharDataValue, relationshipCharTypeIDs?: number[],
        requiredFieldsOnly: boolean = false)
        : ng.IPromise<{ entityID: string, relRecID: number }> {

        if (charTypeID == Consts.CharTypeIDs.relationship) throw new Error('Cannot create a relationship');

        var templateEntityID = Utils.IdBuilder.buildTemplateID(0, charTypeID, templateID);

        return this.createEntityByTemplate(templateEntityID, parentEntityID, parentCurrencyCharData, relationshipCharTypeIDs, requiredFieldsOnly);
    }

    editEntityByID(entityID: string, parentEntityID?: string, parentCurrencyCharData?: Models.ICharDataValue)
        : ng.IPromise<Models.ICharDataCollection> {

        var id = Utils.IdBuilder.splitEntityID(entityID);

        if (id.charTypeID == Consts.CharTypeIDs.relationship) throw new Error('Cannot edit a relationship');

        var deferred = this.$q.defer<Models.ICharDataCollection>();

        this.notifyService.startProgress();
        this.entityService.getEntity(entityID)
            .then((res) => {
                this.editEntity(res.charDataCollection, parentEntityID, parentCurrencyCharData)
                    .then((cdc) => {
                        deferred.resolve(cdc);
                    })
                    .catch((err) => {
                        deferred.reject(err);
                    });
            })
            .finally(() => {
                this.notifyService.endProgress();
            });

        return deferred.promise;
    }

    editEntity(charDataCollection: Models.ICharDataCollection, parentEntityID?: string, parentCurrencyCharData?: Models.ICharDataValue)
        : ng.IPromise<Models.ICharDataCollection> {
        var api = this.$uibModalService.open({
            bindToController: true,
            controllerAs: 'modalVM',
            controller: EditEntityModalController,
            template: require("/assets/app/templates/modals/edit-entity-modal.html"),
            resolve: EditEntityModel(charDataCollection, parentEntityID, parentCurrencyCharData)
        });

        api.opened.then(() => {
            this.EnsureBodyClass();
        });

        return api.result;
    }

    /*
     * edit usage from modal
     * @param charDataCollection is the current charDataCollection
     * @param parentEntityID is the parent to load initial possible relationships from
     * @param parentCurrencyCharData is currency of parent. if not specified will be looked up with parentEntityID (TODO)
     * @param existingRelationships are the previous set of relationships for the specified (charDataCollection) entity
     * @param idForRelationships is redundant to previous as it is where the set of previous relationships will be loaded from
     * @param relationshipCharTypeIDs are the possible charTypes
     * @param relationshipsRequired specifies whether one or more relationships have to be selected
     */
    editEntityWithRelationships(
        charDataCollection: Models.ICharDataCollection,
        parentEntityID: string,
        parentCurrencyCharData?: Models.ICharDataValue,
        existingRelationships: Models.IEntityRelationship[] = [],
        idForRelationships?: string,
        relationshipCharTypeIDs?: number[],
        requiredFieldsOnly: boolean = false,
        singleSelect: boolean = false): ng.IPromise<Models.ICharDataCollection> {

        var api = this.$uibModalService.open({
            bindToController: true,
            controllerAs: 'modalVM',
            controller: EditEntityModalController,
            template: require("/assets/app/templates/modals/edit-entity-modal.html"),
            resolve: EditEntityModel(charDataCollection, parentEntityID, parentCurrencyCharData, existingRelationships, idForRelationships,
                relationshipCharTypeIDs, requiredFieldsOnly, singleSelect)
        });
        api.opened.then(() => {
            this.EnsureBodyClass();
        });
        return api.result;
    }

    private EnsureBodyClass() {
        // ensure that a previous closing modal hasn't removed .modal-open from the body
        this.$timeout(() => {
            angular.element("body").addClass("modal-open");
        }, 1000);
    }

    // TODO: Add Bulk Create modal
    // TODO: Add Copy Entity modal
    // TODO: Add Bulk Edit modal

    bulkEdit(
        parentEntityID: string,
        groupedEntityCharDataCollections: { [entityID: string]: Models.ICharDataCollection[] },
        divID: number,
        charTypeID: number,
        templateID: number,
        parentCurrencyCharData: Models.ICharDataValue,
        relationshipTitles: { [entityID: string]: { [charTypeID: number]: Models.IEntityRecIDTitle[] }; },
        isBulkCreateMode: boolean,
        recordParentRelationships: Models.IEntityRelationship[],
        relatedChildChartypeIDs: number[],
        recordChildRelationships: { [entityID: string]: Models.IEntityRelationship[] },
        sortField: string,
        sortDir: number,
        entityRelRecIDs: { [entityID: string]: string },
        configuredRelatedCharTypeIDs: { [charTypeID: number]: boolean }): ng.IPromise<Models.IJobResponse> {

        var api = this.$uibModalService.open({
            bindToController: true,
            controllerAs: 'bemVM',
            controller: BulkEditModalController,
            template: require("/assets/app/templates/modals/bulk-edit-modal.html"),
            resolve: BulkEditModel(parentEntityID, groupedEntityCharDataCollections, divID, charTypeID, templateID,
                recordParentRelationships, parentCurrencyCharData, relationshipTitles, isBulkCreateMode, recordChildRelationships, relatedChildChartypeIDs, sortField, sortDir, entityRelRecIDs, configuredRelatedCharTypeIDs),
            backdrop: 'static'
        });
        api.opened.then(() => {
            this.EnsureBodyClass();
        });
        return api.result;
    }

    jobProgress(
        jobID: string,
        jobTitle: string,
        progressStatus: string,
        allowJobToCancel: boolean,
        hideProgress: boolean,
        showProgressSpinner: boolean): ng.IPromise<Models.IJobState> {

        var api = this.$uibModalService.open({
            bindToController: true,
            controllerAs: 'jpmVM',
            controller: JobProgressModalController,
            template: require("/assets/app/templates/modals/job-progress-modal.html"),
            resolve: JobProgressModel(jobID, jobTitle, progressStatus, allowJobToCancel, hideProgress, showProgressSpinner)
        });

        api.opened.then(() => {
            this.EnsureBodyClass();
        });

        return api.result;
    }

    bulkExecuteWorkFlow(
        parentEntityID: string,
        charTypeID: number,
        templateID: number,
        recordIDTitlesDictionary: { [key: number]: string },
        processID?: number,
        actionID?: number,
        statusID?: number) {

        var api = this.$uibModalService.open({
            bindToController: true,
            controllerAs: 'bewfVM',
            controller: BulkExecuteWorkFlowModalController,
            template: require("/assets/app/templates/modals/bulk-execute-wf-modal.html"),
            resolve: BulkExecuteWorkFlowModel(parentEntityID, charTypeID, templateID, recordIDTitlesDictionary, processID, actionID, statusID),
            backdrop: 'static'
        });
        api.opened.then(() => {
            this.EnsureBodyClass();
        });
        return api.result;
    }
}

function EditEntityModel(
    cdc: Models.ICharDataCollection,
    parentEntityID?: string,
    parentCurrencyCharData?: Models.ICharDataValue,
    relationships?: Models.IEntityRelationship[],
    idForRels?: string,
    relCharTypeIDs?: number[],
    showRequiredFieldsOnly: boolean = false,
    singleSelect: boolean = false) {

    return {
        charDataCollection: () => {
            return cdc;
        },
        existingRelationships: () => {
            return relationships;
        },
        idForRelationships: () => {
            return idForRels;
        },
        relationshipCharTypeIDs: () => {
            return relCharTypeIDs;
        },
        parentCurrencyCharData: () => {
            return parentCurrencyCharData;
        },
        parentEntityID: () => {
            return parentEntityID;
        },
        showRequiredOnly: () => {
            return showRequiredFieldsOnly;
        },
        singleSelect: () => {
            return singleSelect;
        }
    }
}

function BulkEditModel(
    parentEntityID: string,
    groupedEntityCharDataCollections: { [entityID: string]: Models.ICharDataCollection[] },
    divID: number,
    charTypeID: number,
    templateID: number,
    recordParentRelationships: Models.IEntityRelationship[],
    parentCurrencyCharData?: Models.ICharDataValue,
    relationshipTitles?: {
        [entityID: string]: { [charTypeID: number]: Models.IEntityRecIDTitle[] };
    },
    isBulkCreateMode?: boolean,
    recordChildRelationships?: { [entityID: string]: Models.IEntityRelationship[] },
    relatedChildChartypeIDs?: number[],
    sortField?: string,
    sortDir?: number,
    entityRelRecIDs?: { [entityID: string]: string },
    configuredRelatedCharTypeIDs?: { [charTypeID: number]: boolean }) {
    return {
        parentEntityID: () => {
            return parentEntityID;
        },
        groupedEntityCharDataCollections: () => {
            return groupedEntityCharDataCollections;
        },
        charTypeID: () => {
            return charTypeID;
        },
        divID: () => {
            return divID;
        },
        parentCurrencyCharData: () => {
            return parentCurrencyCharData;
        },
        relationshipTitles: () => {
            return relationshipTitles;
        },
        isBulkCreateMode: () => {
            return isBulkCreateMode;
        },
        templateID: () => {
            return templateID;
        },
        recordChildRelationships: () => {
            return recordChildRelationships;
        },
        recordParentRelationships: () => {
            return recordParentRelationships;
        },
        relatedChildChartypeIDs: () => {
            return relatedChildChartypeIDs;
        },
        sortField: () => {
            return sortField;
        },
        sortDir: () => {
            return sortDir;
        },
        entityRelRecIDs: () => {
            return entityRelRecIDs;
        },
        configuredRelatedCharTypeIDs: () => {
            return configuredRelatedCharTypeIDs;
        }
    }
}

function JobProgressModel(
    jobID: string,
    jobTitle: string,
    progressStatus: string,
    allowJobToCancel: boolean = false,
    hideProgress: boolean = false,
    showProgressSpinner: boolean = false) {

    return {
        jobID: () => {
            return jobID;
        },
        jobTitle: () => {
            return jobTitle;
        },
        progressStatus: () => {
            return progressStatus;
        },
        allowJobToCancel: () => {
            return allowJobToCancel;
        },
        hideProgress: () => {
            return hideProgress;
        },
        showProgressSpinner: () => {
            return showProgressSpinner;
        }
    }
}

function BulkExecuteWorkFlowModel(
    parentEntityID: string,
    charTypeID: number,
    templateID: number,
    recordIDTitlesDictionary: { [key: number]: string },
    processID?: number,
    actionID?: number,
    statusID?: number) {
    return {
        parentEntityID: () => {
            return parentEntityID;
        },
        charTypeID: () => {
            return charTypeID;
        },
        templateID: () => {
            return templateID;
        },
        recordIDTitlesDictionary: () => {
            return recordIDTitlesDictionary;
        },
        processID: () => {
            return processID;
        },
        actionID: () => {
            return actionID;
        },
        statusID: () => {
            return statusID;
        }
    }
}
